import { ExtractComponentProp, FormValues } from '@approvalmax/types';
import { DatePicker, Form, Grid, Select, Spacing, Text, TextField } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { getDepartmentsWithNesting } from 'pages/requestForm/data/qbooks/QBooksDepartment';
import { memo, useEffect, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
    changeQbooksCustomField,
    changeQbooksDate,
    changeQbooksDepartment,
    changeQbooksShippingAddress,
    changeQbooksShippingCustomer,
    changeQbooksShippingMethod,
} from '../../../actions';
import { getRequest, getShowValidationErrors } from '../../../selectors/pageSelectors';
import { getIsRequiredLocation, getQBooksContext } from '../../../selectors/qbooks';
import { useCustomFields, useShipToItems as useShipTo } from './PurchaseOrderShippingDetailsSection.hooks';
import { messages } from './PurchaseOrderShippingDetailsSection.messages';
import { createSchema } from './PurchaseOrderShippingDetailsSection.schema';
import { DepartmentItem } from './PurchaseOrderShippingDetailsSection.styles';

const qa = bemFactory.qa('reqf-qbooks-shipping-section');

export const PurchaseOrderShippingDetailsSection = memo(() => {
    const dispatch = useDispatch();
    const request = useSelector<domain.QBooksPoRequest>(getRequest);
    const qbooksContext = useSelector(getQBooksContext);
    const showErrors = useSelector(getShowValidationErrors);
    const isRequiredLocation = useSelector((state) => getIsRequiredLocation(state, request));
    const { shipToItems, isLoadingShipTo, handleSearchShipTo } = useShipTo();
    const { customFieldsItems, customFieldsValues } = useCustomFields();

    const schema = createSchema(isRequiredLocation);

    const form = useForm<z.infer<typeof schema>>({
        values: {
            date: request.details.date,
            department: request.details.department?.id || '',
            shipTo: request.details.shipping?.customer?.id || '',
            shipVia: request.details.shipping?.method || '',
            shippingAddress: request.details.shipping?.address || '',
            customFields: customFieldsValues,
        },
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const handleFormChange: ExtractComponentProp<typeof Form<FormValues<typeof form>>, 'onChange'> = (
        values,
        rootName,
        path
    ) => {
        switch (rootName) {
            case 'department':
                if (qbooksContext.hasDepartmentFeature) {
                    dispatch(
                        changeQbooksDepartment(
                            qbooksContext.departments.find(({ id }) => id === values.department) || null
                        )
                    );
                }

                break;
            case 'date':
                dispatch(changeQbooksDate(values.date));
                break;
            case 'shipTo':
                dispatch(changeQbooksShippingCustomer(shipToItems.find(({ id }) => id === values.shipTo) || null));
                break;
            case 'shipVia':
                dispatch(changeQbooksShippingMethod(values.shipVia));
                break;
            case 'shippingAddress':
                dispatch(changeQbooksShippingAddress(values.shippingAddress));
                break;
            case 'customFields': {
                const customFieldId = path?.[path.length - 1];

                if (customFieldId) {
                    dispatch(changeQbooksCustomField(customFieldId, values.customFields[customFieldId]));
                }

                break;
            }
        }
    };

    useEffect(() => {
        if (!showErrors) return;

        form.trigger();
    }, [form, showErrors]);

    const departments = useMemo(() => {
        return getDepartmentsWithNesting(qbooksContext.departments);
    }, [qbooksContext.departments]);

    return (
        <>
            <Form form={form} onChange={handleFormChange} data-qa={qa()}>
                <Text font='headline' fontSize='xxsmall' fontWeight='medium' as='h2'>
                    {messages.title}
                </Text>

                <Spacing height={16} />

                <Grid gap={16} gridTemplateColumns='1fr 1fr 1fr'>
                    <Grid gap={16}>
                        <DatePicker
                            name='date'
                            label={messages.dateLabel}
                            placeholder={messages.datePlaceholder}
                            clearable={false}
                            required
                            size='small'
                            qa={qa('date-editor')}
                        />

                        {qbooksContext.hasDepartmentFeature && (
                            <Select
                                name='department'
                                items={departments}
                                itemNameKey='text'
                                label={qbooksContext.departmentField?.text || messages.departmentLabel}
                                placeholder={messages.departmentPlaceholder({
                                    fieldText: qbooksContext.departmentField?.text || '',
                                })}
                                required={isRequiredLocation}
                                size='small'
                                qa={qa('department-editor')}
                                customMenuItem={(item) => (
                                    <DepartmentItem {...item} $nestingLevel={item.nestingLevel} />
                                )}
                            />
                        )}
                    </Grid>

                    <Grid gap={16}>
                        <Select
                            name='shipTo'
                            items={shipToItems}
                            itemNameKey='text'
                            autocomplete
                            label={messages.shipToLabel}
                            placeholder={messages.shipToPlaceholder}
                            size='small'
                            qa={qa('ship-to-editor')}
                            progress={isLoadingShipTo}
                            onInputChange={handleSearchShipTo}
                        />

                        <TextField
                            name='shipVia'
                            label={messages.shipViaLabel}
                            placeholder={messages.shipViaPlaceholder}
                            maxLength={constants.qBooksConstants.SHIPPING_METHOD_MAX_LENGTH}
                            size='small'
                            changeOnBlur
                            qa={qa('ship-via-editor')}
                        />
                    </Grid>

                    <Grid gap={16}>
                        <TextField
                            name='shippingAddress'
                            label={messages.shippingAddressLabel}
                            placeholder={messages.shippingAddressPlaceholder}
                            multiline
                            fullHeight
                            maxLength={constants.qBooksConstants.SHIPPING_ADDRESS_MAX_LENGTH}
                            size='small'
                            changeOnBlur
                            qa={qa('shipping-address-editor')}
                        />
                    </Grid>
                </Grid>

                <Spacing height={16} />

                {customFieldsItems.length > 0 && (
                    <Grid gap={16} gridTemplateColumns='1fr 1fr 1fr' data-qa={qa('custom-fields')}>
                        {customFieldsItems.map((field) => (
                            <TextField
                                key={field.id}
                                name={`customFields.${field.id}`}
                                label={field.text}
                                placeholder={messages.customFieldPlaceholder}
                                size='small'
                                changeOnBlur
                                qa={field.text}
                            />
                        ))}
                    </Grid>
                )}
            </Form>
        </>
    );
});

PurchaseOrderShippingDetailsSection.displayName = 'PurchaseOrderShippingDetailsSection';
